import { useState, useMemo } from "react";
import {
    Box, Typography, Card, CardMedia, Modal, IconButton,
    Paper, Fade, CardContent, Container, Tooltip, useTheme,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";

interface LabReportImage {
    imageData: string;
    metadata: {
        customer: string;
        date: string;
        name: string;
        sheetId: string;
    };
}

interface LabReportsImagesProps {
    images: LabReportImage[];
}
function LabReportsImages({ images }: LabReportsImagesProps) {
    const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const theme = useTheme();

    // Sort images by date (most recent first)
    const sortedImages = useMemo(() => {
        return [...images].sort((a, b) =>
            new Date(b.metadata.date).getTime() - new Date(a.metadata.date).getTime()
        );
    }, [images]);

    const handleSelectImage = (index: number) => {
        setSelectedImageIndex(index);
    };

    const handleOpenModal = () => {
        console.log("Opening modal for image index:", images[selectedImageIndex]);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    return (
        <Box sx={{ py: 2, width: '100%' }}>
            <Card
                elevation={3}
                sx={{
                    width: { xs: '100%', md: '100%' },
                    mx: 'auto',
                    overflow: 'visible'
                }}
            >
                <CardContent>
                    {sortedImages.length === 0 ? (
                        <Typography variant="body1" color="text.secondary">
                            No images available for this lab report.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Main image display */}
                            <Box sx={{ position: 'relative' }}>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        position: 'relative',
                                        height: 300,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: theme.palette.background.paper,
                                        overflow: 'hidden',
                                        borderRadius: 1
                                    }}
                                >
                                    <Fade in={true} key={selectedImageIndex}>
                                        <CardMedia
                                            onClick={handleOpenModal}
                                            component="img"
                                            image={sortedImages[selectedImageIndex].imageData}
                                            alt={`Lab report image ${selectedImageIndex + 1}`}
                                            sx={{
                                                maxHeight: '100%',
                                                maxWidth: '100%',
                                                objectFit: 'contain',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    </Fade>
                                    <Tooltip title="Expand image" arrow placement="top">
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                right: 8,
                                                bottom: 8,
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                    transform: 'scale(1.1)'
                                                },
                                                transition: 'transform 0.2s'
                                            }}
                                            onClick={handleOpenModal}
                                            aria-label="Expand image"
                                        >
                                            <ZoomInIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Paper>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        left: 8,
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                        padding: '2px 6px',
                                        borderRadius: 1,
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    Image {selectedImageIndex + 1} of {sortedImages.length}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 8,
                                        left: 120,
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                        padding: '2px 6px',
                                        borderRadius: 1,
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    {new Date(sortedImages[selectedImageIndex].metadata.date).toLocaleDateString()}
                                </Typography>
                            </Box>

                            {/* Thumbnail bar */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    overflowX: 'auto',
                                    gap: 0.8,
                                    pb: 0.5,
                                    '&::-webkit-scrollbar': {
                                        height: 6,
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: '#f1f1f1',
                                        borderRadius: 3,
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: '#888',
                                        borderRadius: 3,
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        backgroundColor: '#555',
                                    },
                                }}
                            >
                                {sortedImages.map((image, index) => (
                                    <Card
                                        key={image.metadata.sheetId}
                                        onClick={() => handleSelectImage(index)}
                                        sx={{
                                            width: 70,
                                            height: 60,
                                            flexShrink: 0,
                                            cursor: 'pointer',
                                            border: selectedImageIndex === index ? '2px solid #1976d2' : 'none',
                                            opacity: selectedImageIndex === index ? 1 : 0.7,
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: 2,
                                                opacity: 1
                                            }
                                        }}
                                    >
                                        <CardMedia
                                            component="img"
                                            height="100%"
                                            image={image.imageData}
                                            alt={`${image.metadata.name} - ${new Date(image.metadata.date).toLocaleDateString()}`}
                                            sx={{ objectFit: 'cover' }}
                                        />
                                    </Card>
                                ))}
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="image-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Box sx={{
                    position: 'relative',
                    maxWidth: '95%',
                    maxHeight: '95%',
                    outline: 'none',
                }}>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            right: -15,
                            top: -15,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box
                        component="img"
                        src={sortedImages[selectedImageIndex]?.imageData}
                        alt={sortedImages.length > 0 ? (sortedImages[selectedImageIndex]?.metadata.name || "Lab report image") : "No image"}
                        sx={{
                            maxWidth: '100%',
                            maxHeight: 'calc(90vh)',
                            display: 'block',
                            boxShadow: 24,
                        }}
                    />
                </Box>
            </Modal>
        </Box>
    );
}

export default LabReportsImages;